import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Table } from '../components/ui/Table';
import { SubscriptionStatusBadge } from '../components/domain/StatusBadge';
import { Subscription } from '../types';
import { formatDate, getCycleName, isOverdue } from '../utils/calculations';
import { PlusIcon, SearchIcon, EyeIcon, PauseIcon, PlayIcon, XCircleIcon } from 'lucide-react';
export function Subscriptions() {
  const {
    filteredSubscriptions,
    getCustomerById,
    updateSubscription
  } = useData();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(false);
  // Apply filter from navigation state (from Dashboard clicks)
  useEffect(() => {
    const state = location.state as {
      filterStatus?: string;
    } | null;
    if (state?.filterStatus) {
      setStatusFilter(state.filterStatus);
      // Clear the state so back button works normally
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  // Filter subscriptions
  const filteredData = useMemo(() => {
    return filteredSubscriptions.filter(sub => {
      const customer = getCustomerById(sub.customer_id);
      const searchMatch = !searchQuery || sub.id.toLowerCase().includes(searchQuery.toLowerCase()) || customer?.name.toLowerCase().includes(searchQuery.toLowerCase());
      // Handle special "overdue" filter from dashboard
      let statusMatch = true;
      if (statusFilter === 'overdue') {
        statusMatch = sub.status === 'active' && isOverdue(sub.next_billing_date);
      } else {
        statusMatch = statusFilter === 'all' || sub.status === statusFilter;
      }
      return searchMatch && statusMatch;
    });
  }, [filteredSubscriptions, searchQuery, statusFilter, getCustomerById]);
  // Sort by next billing date
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => new Date(a.next_billing_date).getTime() - new Date(b.next_billing_date).getTime());
  }, [filteredData]);
  const handlePause = async (sub: Subscription) => {
    setIsLoading(true);
    try {
      await updateSubscription(sub.id, {
        status: 'paused'
      });
    } catch (error) {
      console.error('Failed to pause subscription:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleResume = async (sub: Subscription) => {
    setIsLoading(true);
    try {
      await updateSubscription(sub.id, {
        status: 'active'
      });
    } catch (error) {
      console.error('Failed to resume subscription:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleCancel = async (sub: Subscription) => {
    setIsLoading(true);
    try {
      await updateSubscription(sub.id, {
        status: 'canceled',
        end_date: new Date()
      });
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const statusOptions = [{
    value: 'all',
    label: 'All Statuses'
  }, {
    value: 'active',
    label: 'Active'
  }, {
    value: 'paused',
    label: 'Paused'
  }, {
    value: 'canceled',
    label: 'Canceled'
  }];
  const columns = [{
    key: 'id',
    header: 'Subscription',
    render: (sub: Subscription) => <Link to={`/subscriptions/${sub.id}`} className="text-blue-600 hover:text-blue-800 font-medium" data-id="element-666">
          {sub.id.slice(0, 12)}...
        </Link>
  }, {
    key: 'customer',
    header: 'Customer',
    render: (sub: Subscription) => {
      const customer = getCustomerById(sub.customer_id);
      return <span className="font-medium" data-id="element-667">{customer?.name || 'Unknown'}</span>;
    }
  }, {
    key: 'cycle',
    header: 'Billing Cycle',
    render: (sub: Subscription) => getCycleName(sub.cycle_days)
  }, {
    key: 'nextBilling',
    header: 'Next Billing',
    render: (sub: Subscription) => {
      const overdue = sub.status === 'active' && isOverdue(sub.next_billing_date);
      return <span className={overdue ? 'text-red-600 font-medium' : ''} data-id="element-668">
            {formatDate(sub.next_billing_date)}
            {overdue && ' (Overdue)'}
          </span>;
    }
  }, {
    key: 'status',
    header: 'Status',
    render: (sub: Subscription) => <SubscriptionStatusBadge status={sub.status} data-id="element-669" />
  }, {
    key: 'actions',
    header: '',
    render: (sub: Subscription) => <div className="flex items-center justify-end gap-1" data-id="element-670">
          <Link to={`/subscriptions/${sub.id}`} data-id="element-671">
            <Button variant="ghost" size="sm" aria-label="View subscription" data-id="element-672">
              <EyeIcon className="w-4 h-4" data-id="element-673" />
            </Button>
          </Link>
          {sub.status === 'active' && <Button variant="ghost" size="sm" onClick={e => {
        e.stopPropagation();
        handlePause(sub);
      }} disabled={isLoading} aria-label="Pause subscription" data-id="element-674">
              <PauseIcon className="w-4 h-4" data-id="element-675" />
            </Button>}
          {sub.status === 'paused' && <Button variant="ghost" size="sm" onClick={e => {
        e.stopPropagation();
        handleResume(sub);
      }} disabled={isLoading} className="text-green-600 hover:text-green-700 hover:bg-green-50" aria-label="Resume subscription" data-id="element-676">
              <PlayIcon className="w-4 h-4" data-id="element-677" />
            </Button>}
          {sub.status !== 'canceled' && <Button variant="ghost" size="sm" onClick={e => {
        e.stopPropagation();
        handleCancel(sub);
      }} disabled={isLoading} className="text-red-600 hover:text-red-700 hover:bg-red-50" aria-label="Cancel subscription" data-id="element-678">
              <XCircleIcon className="w-4 h-4" data-id="element-679" />
            </Button>}
        </div>
  }];
  return <div className="space-y-6" data-id="element-680">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-681">
        <div data-id="element-682">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-683">Subscriptions</h1>
          <p className="text-gray-500 mt-1" data-id="element-684">
            {sortedData.length} subscription{sortedData.length !== 1 ? 's' : ''}{' '}
            found
          </p>
        </div>
        <Link to="/subscriptions/new" data-id="element-685">
          <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-687" />} data-id="element-686">
            New Subscription
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card data-id="element-688">
        <div className="flex flex-col sm:flex-row gap-4" data-id="element-689">
          <div className="flex-1 relative" data-id="element-690">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-691" />
            <Input placeholder="Search by subscription ID or customer..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-id="element-692" />
          </div>
          <div className="w-full sm:w-48" data-id="element-693">
            <Select options={statusOptions} value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-694" />
          </div>
        </div>
      </Card>

      {/* Table */}
      <Table columns={columns} data={sortedData} keyExtractor={sub => sub.id} emptyMessage="No subscriptions found" data-id="element-695" />
    </div>;
}